import { GAME_CONFIG } from '../config';

/**
 * 日志级别枚举
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 100
}

/**
 * 日志条目接口
 */
export interface LogEntry {
  level: LogLevel;
  category: string;
  message: string;
  timestamp: Date;
  data?: any;
}

/**
 * 日志配置接口
 */
export interface LoggerConfig {
  minLevel: LogLevel;
  debugMode: boolean;
  enableConsole: boolean;
  maxHistorySize: number;
}

/**
 * 统一日志管理类
 */
export class Logger {
  private static instance: Logger;
  private config: LoggerConfig;
  private history: LogEntry[] = [];
  
  /**
   * 构造函数
   */
  private constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      minLevel: LogLevel.INFO,
      debugMode: GAME_CONFIG.debugMode || false,
      enableConsole: true,
      maxHistorySize: 1000,
      ...config
    };
    
    if (this.config.debugMode && this.config.minLevel > LogLevel.DEBUG) {
      this.config.minLevel = LogLevel.DEBUG;
    }
  }
  
  /**
   * 获取单例实例
   */
  static getInstance(config?: Partial<LoggerConfig>): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(config);
    } else if (config) {
      // 更新配置
      Logger.instance.config = {
        ...Logger.instance.config,
        ...config
      };
      
      // 调试模式下自动启用DEBUG级别
      if (Logger.instance.config.debugMode && Logger.instance.config.minLevel > LogLevel.DEBUG) {
        Logger.instance.config.minLevel = LogLevel.DEBUG;
      }
    }
    return Logger.instance;
  }
  
  /**
   * 设置调试模式
   * @param enabled 是否启用调试模式
   */
  setDebugMode(enabled: boolean): void {
    this.config.debugMode = enabled;
    
    // 调试模式下自动将日志级别设为DEBUG
    if (enabled && this.config.minLevel > LogLevel.DEBUG) {
      this.config.minLevel = LogLevel.DEBUG;
    }
    
    this.debug('LOGGER', `调试模式已${enabled ? '启用' : '禁用'}`, {
      debugMode: enabled,
      minLevel: this.getLevelLabel(this.config.minLevel)
    });
  }
  
  /**
   * 记录调试级别日志
   */
  debug(category: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, category, message, data);
  }
  
  /**
   * 记录信息级别日志
   */
  info(category: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, category, message, data);
  }
  
  /**
   * 记录警告级别日志
   */
  warn(category: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, category, message, data);
  }
  
  /**
   * 记录错误级别日志
   */
  error(category: string, message: string, data?: any): void {
    this.log(LogLevel.ERROR, category, message, data);
  }
  
  /**
   * 记录API请求
   */
  logApiRequest(provider: string, modelId: string, endpoint: string, body: any): void {
    this.debug('API_REQUEST', `发送请求到${provider}的${modelId}模型`, {
      provider,
      modelId,
      endpoint,
      body: this.safeStringify(body)
    });
  }
  
  /**
   * 记录API响应
   */
  logApiResponse(provider: string, modelId: string, status: number, data: any): void {
    this.debug('API_RESPONSE', `收到${provider}的${modelId}模型响应: ${status}`, {
      provider,
      modelId,
      status,
      data: this.safeStringify(data)
    });
  }
  
  /**
   * 获取日志历史
   */
  getHistory(filterLevel?: LogLevel): LogEntry[] {
    if (filterLevel !== undefined) {
      return this.history.filter(entry => entry.level >= filterLevel);
    }
    return [...this.history];
  }
  
  /**
   * 清除日志历史
   */
  clearHistory(): void {
    this.history = [];
  }
  
  /**
   * 导出日志历史
   */
  exportHistory(filterLevel?: LogLevel): string {
    const entries = filterLevel !== undefined
      ? this.history.filter(entry => entry.level >= filterLevel)
      : this.history;
      
    return JSON.stringify(entries, (key, value) => {
      if (key === 'timestamp' && value instanceof Date) {
        return value.toISOString();
      }
      return value;
    }, 2);
  }
  
  /**
   * 记录日志
   */
  private log(level: LogLevel, category: string, message: string, data?: any): void {
    if (level < this.config.minLevel) {
      return;
    }
    
    const entry: LogEntry = {
      level,
      category,
      message,
      timestamp: new Date(),
      data
    };
    
    // 记录到历史
    this.history.push(entry);
    
    // 限制历史大小
    if (this.history.length > this.config.maxHistorySize) {
      this.history = this.history.slice(-this.config.maxHistorySize);
    }
    
    // 输出到控制台
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }
  }
  
  /**
   * 输出到控制台
   */
  private logToConsole(entry: LogEntry): void {
    const timestamp = this.formatDate(entry.timestamp);
    const prefix = `[${timestamp}] [${this.getLevelLabel(entry.level)}] [${entry.category}]`;
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(`${prefix} ${entry.message}`, entry.data || '');
        break;
      case LogLevel.INFO:
        console.info(`${prefix} ${entry.message}`, entry.data || '');
        break;
      case LogLevel.WARN:
        console.warn(`${prefix} ${entry.message}`, entry.data || '');
        break;
      case LogLevel.ERROR:
        console.error(`${prefix} ${entry.message}`, entry.data || '');
        break;
    }
  }
  
  /**
   * 格式化日期
   */
  private formatDate(date: Date): string {
    return date.toISOString().replace('T', ' ').substring(0, 19);
  }
  
  /**
   * 获取日志级别标签
   */
  private getLevelLabel(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG: return 'DEBUG';
      case LogLevel.INFO: return 'INFO ';
      case LogLevel.WARN: return 'WARN ';
      case LogLevel.ERROR: return 'ERROR';
      default: return 'UNKN ';
    }
  }
  
  /**
   * 安全地将对象转换为字符串
   */
  private safeStringify(obj: any): string {
    if (!obj) return String(obj);
    
    try {
      // 移除可能包含的密钥
      const sanitized = JSON.parse(JSON.stringify(obj, (key, value) => {
        if (
          key.toLowerCase().includes('key') || 
          key.toLowerCase().includes('token') || 
          key.toLowerCase().includes('secret')
        ) {
          return '[REDACTED]';
        }
        return value;
      }));
      
      return JSON.stringify(sanitized);
    } catch (error) {
      return '[无法序列化对象]';
    }
  }
} 